/**
 * Cognito forgot, reset and change password (ENG-6104).
 *
 * Same contract as the sign-in wrapper: failures a form reacts to are thrown
 * as `CognitoSignInError` carrying one of the webapp's existing error codes;
 * anything else (network, misconfiguration) is rethrown unchanged.
 *
 * Cognito emails the reset code with its own template (no Custom Message
 * Lambda), so the reset page takes the 6-digit code, not a signed link.
 */
import { confirmResetPassword, resetPassword, updatePassword } from 'aws-amplify/auth';

import ERROR_CODES from '@/core/apiErrorCodes';

import { ensureCognitoConfigured } from './cognitoSession';
import { CognitoSignInError } from './cognitoSignIn';

const THROTTLED = new Set([
  'TooManyRequestsException',
  'LimitExceededException',
  'TooManyFailedAttemptsException',
]);

function errorName(err: unknown): string | undefined {
  return err instanceof Error ? err.name : undefined;
}

function tooManyAttempts(err: unknown): CognitoSignInError {
  return new CognitoSignInError(ERROR_CODES.AUTH_TOO_MANY_ATTEMPTS, 'Too many attempts', err);
}

function passwordPolicy(err: unknown): CognitoSignInError {
  return new CognitoSignInError(
    ERROR_CODES.AUTH_PASSWORD_REQUIREMENTS,
    'Password does not meet the requirements',
    err
  );
}

function passwordReused(err: unknown): CognitoSignInError {
  return new CognitoSignInError(
    ERROR_CODES.AUTH_PASSWORD_REUSED,
    'Cannot reuse a recent password',
    err
  );
}

function requireCognito(): void {
  if (!ensureCognitoConfigured()) {
    throw new Error('Cognito password call made while the cognito-auth flag is off');
  }
}

// Cognito's answer for a user it has no verified email (or phone) for.
const NO_VERIFIED_ADDRESS = /no registered\/verified email/i;

/**
 * Email a reset code. Resolves the same way whether or not the address has an
 * account (or a verified email Cognito can send to), so the page never reveals
 * which addresses exist. That includes LimitExceeded, which Cognito only
 * raises for an existing user, on a first request and on a resend alike; the
 * page paces resends with its own account-independent cooldown instead.
 */
export async function requestCognitoPasswordReset(email: string): Promise<void> {
  requireCognito();
  try {
    await resetPassword({ username: email.trim() });
  } catch (err) {
    const name = errorName(err);
    const message = err instanceof Error ? err.message : '';
    if (name === 'LimitExceededException' || name === 'UserNotFoundException') return;
    if (name === 'InvalidParameterException' && NO_VERIFIED_ADDRESS.test(message)) return;
    if (name && THROTTLED.has(name)) throw tooManyAttempts(err);
    throw err;
  }
}

/** Set the new password with the emailed code. */
export async function confirmCognitoPasswordReset(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  requireCognito();
  try {
    await confirmResetPassword({
      username: email.trim(),
      confirmationCode: code.trim(),
      newPassword,
    });
  } catch (err) {
    const name = errorName(err);
    if (name && THROTTLED.has(name)) throw tooManyAttempts(err);
    switch (name) {
      case 'InvalidPasswordException':
        throw passwordPolicy(err);
      case 'PasswordHistoryPolicyViolationException':
        throw passwordReused(err);
      // An unknown address can never hold a valid code: same answer as a wrong
      // one, so the code step does not reveal existence either.
      case 'CodeMismatchException':
      case 'ExpiredCodeException':
      case 'UserNotFoundException':
        throw new CognitoSignInError(ERROR_CODES.AUTH_OTP_INVALID, 'Invalid or expired code', err);
      default:
        throw err;
    }
  }
}

/** Change the signed-in user's password; needs an active Cognito session. */
export async function changeCognitoPassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  requireCognito();
  try {
    await updatePassword({ oldPassword, newPassword });
  } catch (err) {
    const name = errorName(err);
    if (name && THROTTLED.has(name)) throw tooManyAttempts(err);
    const message = err instanceof Error ? err.message : '';
    switch (name) {
      case 'NotAuthorizedException':
        if (/attempts exceeded/i.test(message)) throw tooManyAttempts(err);
        // Only a wrong old password; a revoked or expired session is rethrown.
        if (/incorrect username or password/i.test(message)) {
          throw new CognitoSignInError(
            ERROR_CODES.AUTH_INVALID_CREDENTIALS,
            'Current password is incorrect',
            err
          );
        }
        throw err;
      case 'InvalidPasswordException':
        throw passwordPolicy(err);
      case 'PasswordHistoryPolicyViolationException':
        throw passwordReused(err);
      default:
        throw err;
    }
  }
}
